import React, {useEffect, useState} from 'react';
import apiClient from '../../context/apiClient';
import EventImportCard from '../EventImport/EventImportCard';
import FileUploadCard from '../common/FileUploadCard';

const EventImport = () => {
    const [events, setEvents] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [history, setHistory] = useState([]);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [usernameFilter, setUsernameFilter] = useState('');

    const fetchEmployees = async () => {
        try {
            const res = await apiClient.get('/api/employee/all', {withCredentials: true});
            setEmployees(res.data);
        } catch (error) {
            console.error('Fehler beim Laden der Mitarbeitenden', error);
        }
    };

    const fetchHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiClient.get('/api/events/getEventUploadLogInformation');
            setHistory(res.data);
        } catch (err) {
            setError(err.response?.data || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployees();
        fetchHistory();
    }, []);

    const handleUploadSuccess = (data) => {
        const sortedEvents = [...data].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        setEvents(sortedEvents);
        setUploadSuccess(true);
    };

    const filteredHistory = history.filter((entry) => {
        const createdAt = new Date(entry.createdAt);
        const fromDate = dateFrom ? new Date(dateFrom) : null;
        const toDate = dateTo ? new Date(dateTo) : null;

        const matchesDate =
            (!fromDate || createdAt >= fromDate) &&
            (!toDate || createdAt <= toDate);

        const matchesUsername = entry.username
            ?.toLowerCase()
            .includes(usernameFilter.toLowerCase());

        return matchesDate && matchesUsername;
    });

    if (!uploadSuccess) {
        return (
            <main className="flex-1 h-full w-full flex flex-col">
                <div className="flex flex-1 gap-6 overflow-hidden">
                    <div className="md:w-1/3 h-full">
                        <div className="bg-white shadow-md rounded-lg h-full p-6 flex flex-col justify-start">
                            <h3 className="text-lg font-semibold mb-4 text-center">Events hochladen</h3>
                            <p className="text-sm text-gray-600 mb-6 text-center">
                                Laden Sie eine Excel-Datei mit Events hoch. Danach werden die erkannten Events
                                angezeigt.
                            </p>
                            <FileUploadCard
                                uploadUrl="/api/events/uploadEventExcel"
                                onSuccess={handleUploadSuccess}
                            />
                        </div>
                    </div>
                    <div className="md:w-2/3 h-full">
                        <div className="bg-white shadow-md rounded-lg h-full p-6 flex flex-col">
                            <div
                                className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4 shrink-0">
                                <h2 className="text-xl font-semibold">Upload-History</h2>
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                    <label className="text-sm text-gray-600">
                                        Zeitraum von:
                                        <input
                                            type="date"
                                            value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                            className="ml-2 border rounded px-2 py-1 text-sm"
                                        />
                                    </label>
                                    <label className="text-sm text-gray-600">
                                        bis:
                                        <input
                                            type="date"
                                            value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            className="ml-2 border rounded px-2 py-1 text-sm"
                                        />
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Benutzer:in filtern"
                                        value={usernameFilter}
                                        onChange={(e) => setUsernameFilter(e.target.value)}
                                        className="border rounded px-2 py-1 text-sm"
                                    />
                                </div>
                            </div>
                            <div className="overflow-y-auto flex-1 space-y-4">
                                {loading ? (
                                    <p className="text-gray-500">Lade Verlauf…</p>
                                ) : error ? (
                                    <p className="text-red-600"><strong>Fehler:</strong> {String(error)}</p>
                                ) : history.length === 0 ? (
                                    <p className="text-gray-500">Keine Upload-History vorhanden.</p>
                                ) : (
                                    history.slice().reverse().map((entry, index) => (
                                        <div key={index} className="border rounded p-4 bg-gray-50 shadow-sm">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                                                <div>
                                                    <strong>Dateiname:</strong><br/>
                                                    {entry.excelFileName || '-'}
                                                </div>
                                                <div>
                                                    <strong>Benutzer:in:</strong><br/>
                                                    {entry.username || 'Unbekannt'}
                                                </div>
                                                <div>
                                                    <strong>Zeitraum:</strong><br/>
                                                    {entry.firstDate && entry.lastDate
                                                        ? `${new Date(entry.firstDate).toLocaleDateString('de-DE')} – ${new Date(entry.lastDate).toLocaleDateString('de-DE')}`
                                                        : '—'}
                                                </div>
                                                <div>
                                                    <strong>Hochgeladen am:</strong><br/>
                                                    {entry.createdAt
                                                        ? new Date(entry.createdAt).toLocaleString('de-DE')
                                                        : '—'}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        );
    }
    return (
        <main className="fixed top-16 inset-0 bg-gray-100 overflow-y-auto overflow-x-auto">
            <div className="px-6 py-8">
                <div className="h-full overflow-y-auto">
                    {events.length > 0 && (
                        <>
                            <div className="flex justify-center pb-5">
                                <div className="bg-white shadow-md rounded-lg h-full flex flex-col">
                            <span className="text-xl text-gray-800 font-medium">
                                {events.length} Events erkannt
                                <span className="text-gray-500 font-light"> – Ergänzen Sie fehlende Infos</span>
                            </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {events.map((event) => (
                                    <div key={event.id}>
                                        <EventImportCard
                                            event={event}
                                            allEmployees={employees}
                                            onEventSaved={(id) => {
                                                setEvents(prev =>
                                                    prev.filter(e => e.id !== id)
                                                        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
                                                );
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
};
export default EventImport;
